import { type FormEvent } from 'react'
import { useCreateFavorite, useDeleteFavorite, useRenameFavorite } from '../../api/queries'
import type { FavoriteGroup } from '../../api/types'
import { plural } from '../../lib/format'
import { useViewDraft } from '../../lib/useViewDraft'
import { Button } from '../Button'
import { Dialog } from '../Dialog'
import { Spinner } from '../Spinner'

export function GroupNameDialog({
  group,
  onClose: finishClose,
  onCreated,
}: {
  group?: FavoriteGroup
  onClose: () => void
  onCreated?: (group: FavoriteGroup) => void
}) {
  const create = useCreateFavorite()
  const rename = useRenameFavorite()
  const [name, setName, resetName] = useViewDraft(`favorite.${group?.id ?? 'new'}.name`, group?.name ?? '')
  const onClose = () => { resetName(); finishClose() }
  const pending = create.isPending || rename.isPending
  const error = create.error ?? rename.error

  const submit = (e: FormEvent) => {
    e.preventDefault()
    const value = name.trim()
    if (!value || pending) return
    if (group) rename.mutate({ id: group.id, name: value }, { onSuccess: onClose })
    else
      create.mutate(value, {
        onSuccess: (created) => {
          onClose()
          onCreated?.(created)
        },
      })
  }

  return (
    <Dialog
      title={group ? 'Переименовать группу' : 'Новая группа'}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" type="submit" form="favorite-group-name" disabled={pending || !name.trim()}>
            {pending && <Spinner size={14} className="text-accent-fg" />}
            {group ? 'Сохранить' : 'Создать'}
          </Button>
        </>
      }
    >
      <form id="favorite-group-name" onSubmit={submit}>
        <label className="mb-1 block text-xs text-muted" htmlFor="favorite-group-name-input">
          Название
        </label>
        <input
          id="favorite-group-name-input"
          maxLength={80}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Например, На неделю"
          className="h-11 w-full rounded-lg bg-surface px-3 text-[15px] ring-1 ring-line outline-none ring-inset focus:ring-fg/40 desk:h-9 desk:text-sm"
        />
        {error && <p className="mt-2 text-sm text-danger">Не получилось сохранить.</p>}
      </form>
    </Dialog>
  )
}

export function DeleteGroupDialog({
  group,
  onClose,
  onDeleted,
}: {
  group: FavoriteGroup
  onClose: () => void
  onDeleted?: () => void
}) {
  const remove = useDeleteFavorite()
  return (
    <Dialog
      title="Удалить группу?"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="danger"
            disabled={remove.isPending}
            onClick={() =>
              remove.mutate(group.id, {
                onSuccess: () => {
                  onClose()
                  onDeleted?.()
                },
              })
            }
          >
            {remove.isPending && <Spinner size={14} className="text-white" />}
            Удалить
          </Button>
        </>
      }
    >
      <p className="text-sm text-pretty">
        Группа «{group.name}» исчезнет
        {group.count > 0 && (
          <>
            {' '}
            вместе со списком из {group.count} {plural(group.count, 'поста', 'постов', 'постов')}
          </>
        )}
        . Сами посты останутся в ленте.
        {group.agent_url && ' Ссылка для агента перестанет работать.'}
      </p>
      {remove.isError && <p className="mt-2 text-sm text-danger">Не получилось удалить.</p>}
    </Dialog>
  )
}
